import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Plus, Loader2, CheckCircle2, XCircle, Search, Heart, Copy } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";

const APPLIES_LABEL = { keepsake: "Keepsake", legacy: "Legacy", pro: "Pro", all: "Any" };

function CreateModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ code: "", description: "", applies_to: "keepsake", discount_type: "free", discount_value: 100, max_redemptions: 0, expires_at: "", bereavement: false });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    setSaving(true);
    try {
      const body = { ...form, code: form.code.trim().toUpperCase(), max_redemptions: Number(form.max_redemptions) || 0, discount_value: form.discount_type === "free" ? 100 : Number(form.discount_value) || 25, expires_at: form.expires_at || null };
      const r = await api.post("/admin/coupons", body);
      toast.success(`Coupon ${r.data.code} created`);
      onCreated?.(r.data);
      onOpenChange(false);
      setForm({ code: "", description: "", applies_to: "keepsake", discount_type: "free", discount_value: 100, max_redemptions: 0, expires_at: "", bereavement: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="admin-coupon-create-modal">
        <DialogHeader>
          <DialogTitle className="font-display">New coupon</DialogTitle>
          <DialogDescription>Free coupons grant entitlements instantly without a Stripe charge. Percent coupons are recorded for the future Stripe integration.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Code</label>
            <input data-testid="coupon-code-input" value={form.code} onChange={(e) => setForm(f => ({...f, code: e.target.value}))} placeholder="BEREAVEMENT-2026" className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Description</label>
            <input data-testid="coupon-desc-input" value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} placeholder="For families in mourning" className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Applies to</label>
              <select data-testid="coupon-applies-select" value={form.applies_to} onChange={(e) => setForm(f => ({...f, applies_to: e.target.value}))} className="w-full h-10 px-2 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm">
                <option value="keepsake">Keepsake</option>
                <option value="legacy">Legacy</option>
                <option value="pro">Pro</option>
                <option value="all">Any</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Discount</label>
              <select data-testid="coupon-type-select" value={form.discount_type} onChange={(e) => setForm(f => ({...f, discount_type: e.target.value, discount_value: e.target.value === "free" ? 100 : 25}))} className="w-full h-10 px-2 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm">
                <option value="free">Free (100%)</option>
                <option value="percent">Percent (informational)</option>
              </select>
            </div>
          </div>
          {form.discount_type === "percent" && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Percent off</label>
              <input type="number" min="1" max="99" value={form.discount_value} onChange={(e) => setForm(f => ({...f, discount_value: Number(e.target.value)}))} className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm" />
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Recorded only. Full Stripe-integrated percentage discounts land in a later phase.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Max redemptions</label>
              <input data-testid="coupon-max-input" type="number" min="0" value={form.max_redemptions} onChange={(e) => setForm(f => ({...f, max_redemptions: e.target.value}))} placeholder="0 = unlimited" className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Expires (optional)</label>
              <input type="date" value={form.expires_at} onChange={(e) => setForm(f => ({...f, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ""}))} className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
            <input type="checkbox" checked={form.bereavement} onChange={(e) => setForm(f => ({...f, bereavement: e.target.checked}))} className="h-4 w-4 rounded border-[hsl(var(--input))] text-[hsl(var(--accent))] focus:ring-[hsl(var(--ring))]" />
            <span className="inline-flex items-center gap-1"><Heart size={13} className="text-[hsl(var(--destructive))]" /> Tag as bereavement grant</span>
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={() => onOpenChange(false)} className="flex-1 h-10 rounded-xl border border-[hsl(var(--border))] font-semibold text-sm hover:bg-[hsl(var(--muted))]">Cancel</button>
          <button data-testid="coupon-create-submit" onClick={submit} disabled={saving} className="flex-1 h-10 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create coupon
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (active) params.set("active", active);
      const r = await api.get(`/admin/coupons?${params.toString()}`);
      setRows(r.data?.coupons || []); setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error("Could not load coupons");
    }
  }, [search, active]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (c) => {
    setBusy(c.coupon_id);
    try {
      await api.patch(`/admin/coupons/${c.coupon_id}`, { active: !c.active });
      toast.success(!c.active ? "Activated" : "Deactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="admin-coupons">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Coupons</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{total} total. Free coupons grant entitlements instantly, no Stripe roundtrip. Ideal for bereavement grants, press, and partner campaigns.</p>
        </div>
        <button data-testid="admin-coupons-create" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm hover:opacity-90"><Plus size={14} /> New coupon</button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input data-testid="admin-coupons-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or description" className="w-full h-10 pl-9 pr-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--input))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
        </div>
        <select value={active} onChange={(e) => setActive(e.target.value)} className="h-10 px-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--input))] text-sm">
          <option value="">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
        </select>
      </div>

      <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            <tr>
              <th className="px-4 py-2 font-semibold">Code</th>
              <th className="px-4 py-2 font-semibold">Applies to</th>
              <th className="px-4 py-2 font-semibold">Discount</th>
              <th className="px-4 py-2 font-semibold text-right">Redemptions</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2 font-semibold">Created</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-[hsl(var(--muted-foreground))]"><Ticket size={20} className="mx-auto mb-2 opacity-40" />No coupons yet. Create your first one to gift a Keepsake to a bereaved family.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.coupon_id} data-testid={`coupon-row-${c.code}`} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <code className="font-mono text-[13px] font-semibold">{c.code}</code>
                    <button onClick={() => copyCode(c.code)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" title="Copy"><Copy size={12} /></button>
                    {c.bereavement && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"><Heart size={10} /> Bereavement</span>}
                  </div>
                  {c.description && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 truncate max-w-xs">{c.description}</div>}
                </td>
                <td className="px-4 py-2.5">{APPLIES_LABEL[c.applies_to] || c.applies_to}</td>
                <td className="px-4 py-2.5">{c.discount_type === "free" ? <span className="font-semibold text-[hsl(var(--accent))]">FREE (100%)</span> : <span>{c.discount_value}% off <span className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">(info)</span></span>}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{c.redeemed_count || 0}{c.max_redemptions ? <span className="text-[hsl(var(--muted-foreground))]"> / {c.max_redemptions}</span> : ""}</td>
                <td className="px-4 py-2.5">{c.active ? <span className="inline-flex items-center gap-1 text-[hsl(var(--accent))] font-semibold text-xs"><CheckCircle2 size={12} /> Active</span> : <span className="inline-flex items-center gap-1 text-[hsl(var(--muted-foreground))] font-semibold text-xs"><XCircle size={12} /> Inactive</span>}</td>
                <td className="px-4 py-2.5 text-xs text-[hsl(var(--muted-foreground))]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggleActive(c)} disabled={busy === c.coupon_id} className="text-xs font-semibold px-2.5 h-8 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-50">{c.active ? "Deactivate" : "Reactivate"}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateModal open={showCreate} onOpenChange={setShowCreate} onCreated={load} />
    </div>
  );
}
